/**
 * apps/battle/client/src/ReviewScreen.tsx — Read-only review of all battle attempts.
 *
 * FIX 2d: Fetches GET /api/battle/review?cluster=X and displays ALL attempts
 * grouped by question (templateId), newest-first within each group.
 * Reuses feedback-panel CSS classes (score-badge, ao-chip) for visual consistency.
 */
import { useState, useEffect } from 'react';
import { api, ApiError } from './api.js';
import type { ReviewResp, ReviewAttempt } from './api.js';
import { Spinner, ErrorBanner } from './widgets.js';

interface ReviewScreenProps {
  clusterId: string;
  clusterName?: string;
  onBack: () => void;
  // FIX A6: optional retry launcher — "Retry to improve" for cleared questions
  onRetry?: (clusterId: string, templateId: string) => void;
}

/** Group flat attempt list by templateId, preserving first-seen order */
function groupByTemplate(attempts: ReviewAttempt[]): Map<string, ReviewAttempt[]> {
  const groups = new Map<string, ReviewAttempt[]>();
  for (const a of attempts) {
    const g = groups.get(a.templateId);
    if (g) {
      g.push(a);
    } else {
      groups.set(a.templateId, [a]);
    }
  }
  return groups;
}

export function ReviewScreen({ clusterId, clusterName, onBack, onRetry }: ReviewScreenProps) {
  const [data, setData] = useState<ReviewResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api.get<ReviewResp>(`/api/battle/review?cluster=${encodeURIComponent(clusterId)}`)
      .then((resp) => {
        setData(resp);
      })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) {
          setError('Session expired. Please log in again.');
        } else {
          setError('Failed to load review. Please try again.');
        }
      })
      .finally(() => setLoading(false));
  }, [clusterId]);

  const title = clusterName ? `Review — ${clusterId} ${clusterName}` : `Review — Cluster ${clusterId}`;

  return (
    <div>
      {/* Sticky header */}
      <div className="review-header">
        <button
          className="btn btn-secondary"
          data-testid="button-back"
          onClick={onBack}
          style={{ padding: '0.375rem 0.875rem', fontSize: '0.875rem' }}
        >
          ← Back to Map
        </button>
        <div className="review-header-title">{title}</div>
      </div>

      {loading && (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <Spinner size={28} />
        </div>
      )}

      {error && (
        <div style={{ padding: '1.25rem' }}>
          <ErrorBanner msg={error} />
        </div>
      )}

      {data && !loading && (
        <div className="review-body">
          {data.attempts.length === 0 ? (
            <div className="review-empty" data-testid="review-empty">
              No attempts yet for this cluster. Start a battle to see your answers here!
            </div>
          ) : (
            <>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginBottom: '0.875rem' }}>
                {data.attempts.length} attempt{data.attempts.length !== 1 ? 's' : ''} across {groupByTemplate(data.attempts).size} question{groupByTemplate(data.attempts).size !== 1 ? 's' : ''}
              </p>
              {Array.from(groupByTemplate(data.attempts).entries()).map(([templateId, attempts]) => (
                <QuestionGroup
                  key={templateId}
                  templateId={templateId}
                  attempts={attempts}
                  clusterId={clusterId}
                  onRetry={onRetry}
                />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

interface QuestionGroupProps {
  templateId: string;
  attempts: ReviewAttempt[];
  clusterId: string;
  onRetry?: (clusterId: string, templateId: string) => void;
}

function QuestionGroup({ templateId, attempts, clusterId, onRetry }: QuestionGroupProps) {
  // The question stem is the same for all attempts (same template)
  const stem = attempts[0]?.questionText ?? '(question text unavailable)';
  const markValue = attempts[0]?.markValue ?? 0;
  const battleType = attempts[0]?.battleType ?? '';
  const modelAnswer = attempts.find((a) => a.modelAnswer)?.modelAnswer;

  // Sort attempts by attemptNumber ascending for display
  const sorted = [...attempts].sort((a, b) => a.attemptNumber - b.attemptNumber);

  // FIX A5/A6: determine cleared status (any attempt cleared = cleared).
  // Best score = MAX score across all attempts.
  const isCleared = sorted.some((a) => a.cleared);

  return (
    <div className="review-question-group" data-testid="review-question-group">
      <div className="review-question-stem">
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.375rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <span className="ao-chip">{battleType}</span>
          <span className="ao-chip">{markValue} mark{markValue !== 1 ? 's' : ''}</span>
          {isCleared && <span style={{ color: 'var(--success)', fontSize: '0.75rem', fontWeight: 700 }}>✓ Cleared</span>}
          {/* FIX A6: Retry to improve for cleared questions; Try again for uncleared */}
          {onRetry && (
            <button
              className="btn btn-secondary"
              style={{ padding: '0.2rem 0.6rem', fontSize: '0.75rem', marginLeft: 'auto' }}
              onClick={() => onRetry(clusterId, templateId)}
              data-testid={`btn-retry-${templateId}`}
            >
              {isCleared ? 'Retry to improve' : 'Try again'}
            </button>
          )}
        </div>
        {stem}
      </div>

      {sorted.map((attempt) => (
        <AttemptRow key={attempt.attemptNumber} attempt={attempt} />
      ))}

      {modelAnswer && (
        <div style={{ padding: '0 1rem 0.75rem' }}>
          <div className="review-model-answer">
            <div className="review-model-answer-label">Model Answer</div>
            <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{modelAnswer}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function AttemptRow({ attempt }: { attempt: ReviewAttempt }) {
  const aoChips: { label: string; value: number | null }[] = [
    { label: 'AO1', value: attempt.ao1 },
    { label: 'AO2', value: attempt.ao2 },
    { label: 'AO3', value: attempt.ao3 },
    { label: 'AO4', value: attempt.ao4 },
  ].filter((c) => c.value !== null && c.value !== undefined) as { label: string; value: number }[];

  const dateStr = attempt.createdAt
    ? new Date(attempt.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : '';

  return (
    <div className="review-attempt" data-testid="review-attempt">
      <div className="review-attempt-header">
        <span className="review-attempt-label">Attempt {attempt.attemptNumber}</span>
        <span
          className={`score-badge ${attempt.cleared ? 'cleared' : 'failed'}`}
          style={{ fontSize: '0.75rem' }}
        >
          {attempt.score ?? '—'}/{attempt.markValue}
        </span>
        {attempt.cleared && (
          <span style={{ color: 'var(--success)', fontSize: '0.75rem', fontWeight: 700 }}>✓ Cleared</span>
        )}
        {aoChips.map((c) => (
          <span key={c.label} className="ao-chip" style={{ fontSize: '0.7rem' }}>
            {c.label}: {c.value}
          </span>
        ))}
        {dateStr && (
          <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem', marginLeft: 'auto' }}>{dateStr}</span>
        )}
      </div>
      <div className="review-student-answer">{attempt.studentResponse || '(no answer recorded)'}</div>
    </div>
  );
}
